import React, { useCallback, useEffect, useState } from 'react';
import TdButton from '../Button';
import { STATE_LABEL, STATE_TONE, cn } from '../model/derive';
import { KST, formatDue, fromInputValue, toInputValue } from '../model/time';
import { Field, Modal, ModalButton, Select, TextArea, TextInput } from '../Modal';
import { StatusChip } from '../primitives';
import TaskListEditor from '../TaskListEditor';
import TaskListGroups from '../TaskListGroups';
import { SOURCE_LABEL, scopeRule, typeOfBase } from '../model/taskList';
import { EmptyBox, StepCard, WarnBox } from './SetupSteps';
import { EXTEND_REASONS, TEAM_START_H, addHours, lockedNotice, setupMode } from './opsModel';

/**
 * Tasks — 시작 뒤 (운영진 규칙 §2). Setup 계획이 아니라 팀 워크보드에서 진행 중인 태스크를 다룬다.
 *   재배정: 검토 중·승인·취소된 태스크는 안 된다. 새 담당자가 다시 수락한다 (자동 수락 없음)
 *   받는 사람 변경: 승인·취소 전까지. 팀장으로도
 *   마감 변경: 외부 사유 3종일 때 늦추기만. 늦춘 태스크는 기한 준수 집계에서 빠진다
 *   취소: 사유 필수
 *   Task List: 과제 항목만 (기본 항목 잠금)
 * 바꿀 때마다 태스크에 기록이 남고, 관련된 팀원에게 알림이 간다.
 */

const NPC = 'npc';
const CLOSED = ['approved', 'cancelled'];
const OPEN = ['assigned', 'declined', 'in_progress', 'rework_requested', 'submitted'];
const at = (iso) => (iso ? `${formatDue(iso, KST)} KST` : '–');
const reasonLabel = (key) => EXTEND_REASONS.find((r) => r.key === key)?.label ?? key;

const COLS =
  'grid grid-cols-[56px_minmax(200px,1fr)_160px_160px_120px_170px_64px] items-center gap-3';

const ScopeView = ({ task, teamStart, busy, error, onBack, onSave }) => {
  const base = task.baseItems ?? [];
  const items = task.taskList ?? [];
  const rule = scopeRule(task, new Date(), new Date(task.dueAt), teamStart);
  const [rows, setRows] = useState(() =>
    items.map((text, i) => ({ text, origin: i, source: task.taskSources?.[i] ?? 'ops' })),
  );
  const [reason, setReason] = useState('');
  const kept = rows.filter((r) => r.text.trim());
  const clean = kept.map((r) => r.text.trim());
  const changed = clean.join('\n') !== items.join('\n');
  const onlyRemoved = !rule.deleteOnly || kept.every((r) => r.origin != null);
  const ok = rule.open && base.length + clean.length > 0 && changed && onlyRemoved && reason.trim();
  return (
    <Modal
      title={`Task List · ${task.code ? `${task.code} · ` : ''}${task.title}`}
      subtitle='Every assignee gets notified.'
      width='max-w-[560px]'
      onClose={onBack}
      footer={
        <>
          <ModalButton onClick={onBack}>Back</ModalButton>
          <ModalButton
            primary
            disabled={busy || !ok}
            onClick={() =>
              onSave({
                type: 'scope',
                items: clean,
                origins: kept.map((r) => r.origin),
                reason: reason.trim(),
              })
            }
          >
            Save Task List
          </ModalButton>
        </>
      }
    >
      {!rule.open && <WarnBox items={[rule.why]} />}
      {rule.open && rule.deleteOnly && (
        <WarnBox items={['Less than 1 hour to the due time. You can only remove items now.']} />
      )}
      {rule.open && !rule.deleteOnly && task.state === 'rework_requested' && (
        <WarnBox items={['Changes were requested. Add or remove items, but don’t reword them.']} />
      )}
      {base.length > 0 && <TaskListGroups type={typeOfBase(base)} base={base} items={[]} />}
      {rule.open && (
        <Field label='Task items' htmlFor='live-scope'>
          <TaskListEditor
            id='live-scope'
            items={rows}
            onChange={setRows}
            startAt={base.length}
            textOf={(r) => r.text}
            withText={(r, v) => ({ ...r, text: v })}
            blankRow={{ text: '', origin: null, source: 'ops' }}
            isLocked={(r) => r.origin != null && rule.keepText}
            tagOf={(r) => SOURCE_LABEL[r.source]}
            canAdd={!rule.deleteOnly}
          />
        </Field>
      )}
      <Field label='Reason' required htmlFor='live-scope-reason'>
        <TextArea
          id='live-scope-reason'
          value={reason}
          maxLength={500}
          onChange={(e) => setReason(e.target.value)}
        />
      </Field>
      {error && <p className='text-[13px] text-functional-failed04'>{error}</p>}
    </Modal>
  );
};

const EditModal = ({ task, team, tasks, endAt, teamStart, onClose, onEdit }) => {
  const people = team.members.filter((m) => m.id != null);
  const active = people.filter((m) => m.status !== 'dropped');
  const nameOf = (id) =>
    id == null ? 'Team lead Kim' : (people.find((m) => m.id === id)?.name ?? 'Someone');
  const [assignee, setAssignee] = useState(String(task.assigneeId));
  const [receiver, setReceiver] = useState(task.receiverId == null ? NPC : String(task.receiverId));
  const [due, setDue] = useState(toInputValue(new Date(task.dueAt), KST));
  const [dueReason, setDueReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [scoping, setScoping] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const reviewing = task.state === 'submitted';
  const accepted = ['in_progress', 'rework_requested'].includes(task.state);
  const assigneeChanged = assignee !== String(task.assigneeId);
  const receiverChanged = receiver !== (task.receiverId == null ? NPC : String(task.receiverId));
  const dueDate = fromInputValue(due, KST);
  const dueChanged = dueDate && dueDate.getTime() !== new Date(task.dueAt).getTime();
  // 늦추기 = 외부 사유 필요(기한 준수 집계 제외) · 당기기 = 사유 없이, 지금 이후로만
  const dueLater = dueChanged && dueDate > new Date(task.dueAt);
  const dueEarlier = dueChanged && !dueLater;
  const after = tasks.filter(
    (t) => (t.predecessorIds ?? []).includes(task.id) && !CLOSED.includes(t.state),
  );

  // 막는 것 — 저장 버튼이 꺼지고 이유를 보여준다
  const blocks = [
    assigneeChanged &&
      assignee === receiver &&
      'The assignee and the receiver can’t be the same person.',
    dueEarlier && dueDate <= new Date() && 'Pick a due time that hasn’t passed.',
    dueChanged &&
      dueDate > new Date(endAt) &&
      `The due time can’t be after the project ends (${at(endAt)}).`,
    dueLater && !dueReason && 'Pick an outside reason to move the due time later.',
  ].filter(Boolean);
  const changed = assigneeChanged || receiverChanged || dueChanged;

  // 알려 두는 것 — 저장은 된다
  const warnings = [
    assigneeChanged &&
      accepted &&
      `${nameOf(task.assigneeId)} already accepted this task. ${nameOf(Number(assignee))} has to accept it again, and the acceptance timer restarts.`,
    assigneeChanged &&
      !accepted &&
      `${nameOf(Number(assignee))} gets this as a new request and has to accept it.`,
    receiverChanged &&
      reviewing &&
      `${receiver === NPC ? 'You (Team lead Kim)' : nameOf(Number(receiver))} gets the submitted handoff for review right away.`,
    receiverChanged && receiver === NPC && 'The handoff moves to the Lead inbox.',
    dueLater && 'This task will be left out of on-time stats.',
    dueEarlier &&
      `The due time moves earlier, so ${nameOf(task.assigneeId)} has less time. The assignees are notified.`,
  ];

  const run = async (calls) => {
    setBusy(true);
    setError('');
    try {
      // 하나씩 — 앞의 것이 실패하면 멈춘다
      for (const body of calls) await onEdit(task.id, body);
      onClose();
    } catch (e) {
      setError(e.response?.data?.msg || 'Couldn’t save. Refresh and try again.');
    } finally {
      setBusy(false);
    }
  };
  const save = () =>
    run(
      [
        assigneeChanged && { type: 'reassign', memberId: assignee },
        receiverChanged && { type: 'receiver', memberId: receiver },
        dueChanged && {
          type: 'due',
          dueAt: dueDate.toISOString(),
          reason: dueLater ? dueReason : null,
        },
      ].filter(Boolean),
    );

  if (scoping) {
    return (
      <ScopeView
        task={task}
        teamStart={teamStart}
        busy={busy}
        error={error}
        onBack={() => setScoping(false)}
        onSave={(body) => run([body])}
      />
    );
  }

  if (deleting) {
    return (
      <Modal
        title={`Delete “${task.title}”?`}
        subtitle='It hasn’t been submitted yet, so it’s removed from the board. This can’t be undone.'
        onClose={() => setDeleting(false)}
        footer={
          <>
            <ModalButton onClick={() => setDeleting(false)}>Back</ModalButton>
            <TdButton variant='danger' disabled={busy} onClick={() => run([{ type: 'delete' }])}>
              Delete task
            </TdButton>
          </>
        }
      >
        <WarnBox
          items={[
            `${nameOf(task.assigneeId)} is notified. Its comments are deleted too.`,
            after.length > 0 &&
              `${after.length} task${after.length > 1 ? 's come' : ' comes'} after this one (${after
                .map((t) => t.code || t.title)
                .join(', ')}). They no longer wait for it.`,
          ]}
        />
        {error && <p className='text-[13px] text-functional-failed04'>{error}</p>}
      </Modal>
    );
  }

  if (cancelling) {
    return (
      <Modal
        title={`Cancel “${task.title}”?`}
        subtitle='The task stays on the board as Cancelled. This can’t be undone.'
        onClose={() => setCancelling(false)}
        footer={
          <>
            <ModalButton onClick={() => setCancelling(false)}>Back</ModalButton>
            <TdButton
              variant='danger'
              disabled={busy || !cancelReason.trim()}
              onClick={() => run([{ type: 'cancel', reason: cancelReason.trim() }])}
            >
              Cancel task
            </TdButton>
          </>
        }
      >
        <WarnBox
          items={[
            `${nameOf(task.assigneeId)} is notified.`,
            reviewing && 'It’s under review now. The review stops.',
            after.length > 0 &&
              `${after.length} task${after.length > 1 ? 's come' : ' comes'} after this one (${after
                .map((t) => t.code || t.title)
                .join(', ')}). Check their plan.`,
          ]}
        />
        <Field label='Reason' required htmlFor='cancel-reason'>
          <TextArea
            id='cancel-reason'
            value={cancelReason}
            maxLength={500}
            onChange={(e) => setCancelReason(e.target.value)}
          />
        </Field>
        {error && <p className='text-[13px] text-functional-failed04'>{error}</p>}
      </Modal>
    );
  }

  return (
    <Modal
      title={`${task.code ? `${task.code} · ` : ''}${task.title}`}
      subtitle={`${STATE_LABEL[task.state]} · due ${at(task.dueAt)}`}
      width='max-w-[520px]'
      onClose={onClose}
      footer={
        <>
          <span className='mr-auto flex gap-1'>
            {/* 한 번도 제출되지 않았으면 지운다. 제출 기록이 있으면 취소로 남긴다 */}
            {task.deletable && (
              <TdButton
                variant='ghost'
                onClick={() => setDeleting(true)}
                className='text-functional-failed04 hover:text-functional-failed04'
              >
                Delete…
              </TdButton>
            )}
            <TdButton
              variant='ghost'
              onClick={() => setCancelling(true)}
              className='text-functional-failed04 hover:text-functional-failed04'
            >
              Cancel task…
            </TdButton>
          </span>
          <ModalButton onClick={onClose}>Close</ModalButton>
          <ModalButton primary disabled={busy || !changed || blocks.length > 0} onClick={save}>
            Save
          </ModalButton>
        </>
      }
    >
      <Field
        label='Assignee'
        htmlFor='live-assignee'
        hint={reviewing ? 'Under review — change the receiver instead.' : undefined}
      >
        <Select
          id='live-assignee'
          value={assignee}
          disabled={reviewing}
          onChange={(e) => setAssignee(e.target.value)}
        >
          {/* 이탈한 지금 담당자도 보여야 바뀐 걸 안다 */}
          {!active.some((m) => m.id === task.assigneeId) && (
            <option value={String(task.assigneeId)} disabled>
              {nameOf(task.assigneeId)} (dropped)
            </option>
          )}
          {active.map((m) => (
            <option key={m.id} value={String(m.id)}>
              {m.name}
              {m.staff ? ' · Staff' : ''}
            </option>
          ))}
        </Select>
      </Field>
      <Field label='Receiver' htmlFor='live-receiver'>
        <Select id='live-receiver' value={receiver} onChange={(e) => setReceiver(e.target.value)}>
          {task.receiverId != null && !active.some((m) => m.id === task.receiverId) && (
            <option value={String(task.receiverId)} disabled>
              {nameOf(task.receiverId)} (dropped)
            </option>
          )}
          {active
            .filter((m) => String(m.id) !== assignee)
            .map((m) => (
              <option key={m.id} value={String(m.id)}>
                {m.name}
                {m.staff ? ' · Staff' : ''}
              </option>
            ))}
          <option value={NPC}>Team lead Kim (you)</option>
        </Select>
      </Field>
      <div className='grid gap-3 sm:grid-cols-2'>
        <Field
          label='Due (KST)'
          htmlFor='live-due'
          hint='Later needs an outside reason. Earlier is fine with a notice.'
        >
          <TextInput
            id='live-due'
            type='datetime-local'
            value={due}
            min={toInputValue(new Date(), KST)}
            max={toInputValue(new Date(endAt), KST)}
            onChange={(e) => setDue(e.target.value)}
          />
        </Field>
        <Field label='Reason' required={!!dueLater} htmlFor='live-due-reason'>
          <Select
            id='live-due-reason'
            value={dueReason}
            disabled={!dueLater}
            onChange={(e) => setDueReason(e.target.value)}
          >
            <option value=''>Pick a reason</option>
            {EXTEND_REASONS.map((r) => (
              <option key={r.key} value={r.key}>
                {r.label}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <div className='flex flex-col gap-1.5'>
        <div className='flex items-center justify-between gap-3'>
          <span className='text-[13px] font-medium text-text-primary'>Task List</span>
          <TdButton variant='soft' size='s' onClick={() => setScoping(true)}>
            Edit Task List
          </TdButton>
        </div>
        <TaskListGroups
          type={typeOfBase(task.baseItems)}
          base={task.baseItems ?? []}
          items={task.taskList ?? []}
          sources={task.taskSources}
        />
      </div>
      <WarnBox items={warnings} />
      {blocks.length > 0 && (
        <ul className='flex flex-col gap-1 text-[13px] text-functional-failed04'>
          {blocks.map((b) => (
            <li key={b}>{b}</li>
          ))}
        </ul>
      )}
      {error && <p className='text-[13px] text-functional-failed04'>{error}</p>}
    </Modal>
  );
};

const LiveTasksStep = ({ project, api }) => {
  const mode = setupMode(project);
  // 시작 뒤 · 끝난 뒤에도 고친다 (끝났으면 경고)
  const readOnly = mode === 'draft';
  const [tasks, setTasks] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [pickedTeamId, setTeamId] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api
      .getLiveTasks()
      .then((list) => {
        setTasks(list ?? []);
        setLoadError('');
      })
      .catch(() => setLoadError('Couldn’t load the live tasks. Refresh the page.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id]);
  useEffect(() => {
    load();
    // 팀원이 보드에서 움직이니 창으로 돌아오면 새로 받는다
    window.addEventListener('focus', load);
    return () => window.removeEventListener('focus', load);
  }, [load]);

  const teamId = project.teams.some((t) => t.id === pickedTeamId)
    ? pickedTeamId
    : (project.teams[0]?.id ?? null);
  const team = project.teams.find((t) => t.id === teamId);
  const list = (tasks ?? []).filter((t) => t.teamId === teamId);
  const memberOf = (id) => team?.members.find((m) => m.id === id);
  const dropped = (id) => memberOf(id)?.status === 'dropped';

  // 이탈한 사람에게 걸린 열린 태스크 — 운영진이 직접 옮겨야 한다 (자동으로 옮기지 않는다)
  const strandedAssignee = list.filter((t) => OPEN.includes(t.state) && dropped(t.assigneeId));
  const strandedReceiver = list.filter(
    (t) => OPEN.includes(t.state) && t.receiverId != null && dropped(t.receiverId),
  );

  const edit = async (taskId, body) => setTasks(await api.editLiveTask(taskId, body));

  const person = (id, isReceiver) => {
    if (isReceiver && id == null) return <span className='text-text-secondary'>Team lead Kim</span>;
    const m = memberOf(id);
    if (!m) return <span className='text-text-placeholder'>Unknown</span>;
    return (
      <span className='flex min-w-0 items-center gap-1.5'>
        <span className={cn('truncate', m.status === 'dropped' && 'text-functional-failed04')}>
          {m.name}
        </span>
        {m.status === 'dropped' && (
          <span className='shrink-0 text-[12px] text-functional-failed04'>dropped</span>
        )}
        {m.staff && <StatusChip chip={{ label: 'Staff', tone: 'outline' }} />}
      </span>
    );
  };

  return (
    <StepCard
      title='Tasks'
      desc='Live tasks on each team’s board. Every change is recorded and the people involved are notified.'
      notice={
        mode === 'ended'
          ? 'The project has ended. Members can’t act on these tasks anymore, but your changes still show on the workboard and in reports.'
          : readOnly
            ? lockedNotice(mode)
            : null
      }
    >
      {!readOnly && (
        <WarnBox
          items={[
            'These are the live board tasks, not the plan. Members see your changes right away.',
            'Reassigning restarts the task: the new assignee has to accept it again. Tasks under review can’t be reassigned. Change the receiver instead.',
            'Moving a due time later needs an outside reason (system outage, member dropout, admin error), and the task is left out of on-time stats. Moving it earlier is allowed — the assignees are notified.',
            'Tasks that were never submitted can be deleted. Submitted ones can only be cancelled.',
            'Approved and cancelled tasks can’t be changed.',
          ]}
        />
      )}
      {project.teams.length === 0 ? (
        <EmptyBox title='No teams' desc='This project has no teams.' />
      ) : (
        <>
          <div
            role='tablist'
            aria-label='Team'
            className='flex w-fit flex-wrap gap-0.5 rounded-xl bg-grayscale-gray02/70 p-1'
          >
            {project.teams.map((t) => (
              <button
                key={t.id}
                type='button'
                role='tab'
                aria-selected={t.id === teamId}
                onClick={() => setTeamId(t.id)}
                className={cn(
                  'h-8 rounded-lg px-3.5 text-[14px] transition-colors',
                  t.id === teamId
                    ? 'bg-grayscale-gray00 font-medium text-text-primary shadow-[0_1px_3px_rgba(0,0,0,0.12)]'
                    : 'text-text-secondary hover:text-text-primary',
                )}
              >
                {t.name}
              </button>
            ))}
          </div>

          {!readOnly && (
            <WarnBox
              items={[
                strandedAssignee.length > 0 &&
                  `${strandedAssignee.length} open task${strandedAssignee.length > 1 ? 's belong' : ' belongs'} to dropped members (${strandedAssignee
                    .map((t) => t.code || t.title)
                    .join(', ')}). Reassign ${strandedAssignee.length > 1 ? 'them' : 'it'}.`,
                strandedReceiver.length > 0 &&
                  `${strandedReceiver.length} handoff${strandedReceiver.length > 1 ? 's go' : ' goes'} to dropped members (${strandedReceiver
                    .map((t) => t.code || t.title)
                    .join(', ')}). Change the receiver.`,
              ]}
            />
          )}

          {loadError ? (
            <p className='text-[14px] text-functional-failed04'>{loadError}</p>
          ) : tasks == null ? (
            <p className='py-8 text-center text-[14px] text-text-secondary'>Loading…</p>
          ) : list.length === 0 ? (
            <EmptyBox
              title={`No live tasks for ${team?.name}`}
              desc='Tasks appear here once the project starts and the team has people for each job.'
            />
          ) : (
            <div className='overflow-x-auto rounded-xl border border-green-deep03/[0.08] bg-grayscale-gray00 shadow-4'>
              <div className='min-w-[960px]'>
                <div
                  className={cn(COLS, 'px-4 py-2.5 text-[12px] font-medium text-text-secondary')}
                >
                  <span>#</span>
                  <span>Task</span>
                  <span>Assignee</span>
                  <span>Receiver</span>
                  <span>State</span>
                  <span>Due</span>
                  <span />
                </div>
                {list.map((t) => {
                  const closed = CLOSED.includes(t.state);
                  return (
                    <div
                      key={t.id}
                      className={cn(
                        COLS,
                        'min-h-12 border-t border-green-deep03/[0.08] px-4 py-2 text-[14px] text-text-primary',
                        closed && 'text-text-secondary',
                      )}
                    >
                      <span className='text-[13px] font-medium text-green-deep04'>
                        {t.code || '–'}
                      </span>
                      <span className='truncate'>{t.title}</span>
                      <span className='flex min-w-0 flex-col text-[13px]'>
                        {/* 공동 담당자면 모두 */}
                        {(t.assigneeIds?.length ? t.assigneeIds : [t.assigneeId]).map((id) => (
                          <span key={id}>{person(id, false)}</span>
                        ))}
                      </span>
                      <span className='min-w-0 text-[13px]'>{person(t.receiverId, true)}</span>
                      <span>
                        <StatusChip
                          chip={{ label: STATE_LABEL[t.state], tone: STATE_TONE[t.state] }}
                        />
                      </span>
                      <span className='flex flex-col text-[13px]'>
                        {at(t.dueAt)}
                        {t.extendedReason && (
                          <span className='text-[12px] text-text-secondary'>
                            Extended · {reasonLabel(t.extendedReason)}
                          </span>
                        )}
                      </span>
                      {readOnly ? (
                        <span />
                      ) : (
                        <button
                          type='button'
                          disabled={closed}
                          title={
                            closed ? `${STATE_LABEL[t.state]} tasks can’t be changed` : undefined
                          }
                          onClick={() => setEditing(t)}
                          className='text-[13px] font-medium text-text-primary hover:text-green-deep03 disabled:cursor-not-allowed disabled:text-text-placeholder'
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
      {editing && team && (
        <EditModal
          task={editing}
          team={team}
          tasks={list}
          endAt={project.endAt}
          teamStart={new Date(addHours(project.leaderStartAt, TEAM_START_H))}
          onClose={() => setEditing(null)}
          onEdit={edit}
        />
      )}
    </StepCard>
  );
};

export default LiveTasksStep;
